import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Flex,
  Select,
  Input,
  useToast,
  Spinner,
  Text,
  IconButton,
  HStack,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Progress
} from '@chakra-ui/react';
import { FaEye, FaDownload, FaSync, FaChartLine } from 'react-icons/fa';
import { MdEmail, MdOpenInNew, MdTouchApp } from 'react-icons/md';
import api, { getToken, API_BASE_URL } from '../../../../../services/api';
import MetricsCards from './components/MetricsCards';
import { useNavigate } from 'react-router-dom';
import './CampaignAnalytics.css';

const CampaignAnalytics = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [aggregateMetrics, setAggregateMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedState, setSelectedState] = useState('BA');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [refreshing, setRefreshing] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCampaignReport();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchCampaignReport(true);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [selectedState, dateRange]);

  const fetchCampaignReport = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params = new URLSearchParams({
        state: selectedState
      });

      if (dateRange.startDate) {
        params.append('startDate', dateRange.startDate);
      }
      if (dateRange.endDate) {
        params.append('endDate', dateRange.endDate);
      }

      const response = await api.get(`/analytics/report?${params}`);
      
      // Add proper response validation
      if (response && response.success) {
        setCampaigns(response.report.campaigns);
        setAggregateMetrics(response.report.aggregateMetrics);
      } else {
        throw new Error(response?.message || 'Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching campaign report:', error);
      
      // Better error message handling
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Erro ao buscar dados do relatório';
      
      toast({
        title: 'Erro ao carregar relatório',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const viewCampaignDetails = (campaignId) => {
    navigate(`/admin/email/analytics/campaign/${campaignId}`);
  };

  const exportCampaignReport = async (campaignId) => {
    try {
      // Use fetch directly for blob responses
      const response = await fetch(`${API_BASE_URL}/analytics/campaigns/${campaignId}/export`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export report');
      }

      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campaign-${campaignId}-report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: 'Relatório exportado',
        description: 'O arquivo CSV foi baixado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error exporting report:', error);
      toast({
        title: 'Erro ao exportar',
        description: 'Não foi possível exportar o relatório',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'completed': 'green',
      'sending': 'blue',
      'draft': 'gray',
      'paused': 'yellow',
      'failed': 'red'
    };

    const statusLabels = {
      'completed': 'Concluída',
      'sending': 'Enviando',
      'draft': 'Rascunho',
      'paused': 'Pausada',
      'failed': 'Falhou'
    };

    return (
      <Badge colorScheme={statusColors[status] || 'gray'}>
        {statusLabels[status] || status}
      </Badge>
    );
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPercentage = (value) => {
    return `${parseFloat(value || 0).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <HStack spacing={4} align="center">
          <Button
            onClick={() => navigate('/admin/email')}
            variant="outline"
            colorScheme="gray"
            size="sm"
            leftIcon={<span>←</span>}
          >
            Voltar
          </Button>
          <Heading size="lg">
            <FaChartLine style={{ display: 'inline', marginRight: '10px' }} />
            Análise de Campanhas de Email
          </Heading>
        </HStack>
        <HStack spacing={3}>
          <IconButton
            icon={<FaSync />}
            onClick={() => fetchCampaignReport()}
            isLoading={refreshing}
            colorScheme="blue"
            variant="outline"
            size="sm"
            aria-label="Atualizar"
            title="Atualizar dados"
          />
        </HStack>
      </Flex>

      {/* Filters */}
      <Box bg="gray.50" p={4} borderRadius="md" mb={6}>
        <HStack spacing={4}>
          <Select
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            width="150px"
            bg="white"
          >
            <option value="BA">Bahia</option>
            <option value="MA">Maranhão</option>
          </Select>
          <Input
            type="date"
            placeholder="Data início"
            value={dateRange.startDate}
            onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
            width="200px"
            bg="white"
          />
          <Input
            type="date"
            placeholder="Data fim"
            value={dateRange.endDate}
            onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
            width="200px"
            bg="white"
          />
          <Button
            size="sm"
            onClick={() => setDateRange({ startDate: '', endDate: '' })}
            variant="ghost"
          >
            Limpar filtros
          </Button>
        </HStack>
      </Box>

      {/* Aggregate Metrics */}
      {aggregateMetrics && (
        <MetricsCards metrics={aggregateMetrics} />
      )}

      {/* Campaigns Table */}
      <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
        <Box p={4} borderBottom="1px" borderColor="gray.200">
          <Heading size="md">Campanhas</Heading>
        </Box>
        
        <Box overflowX="auto">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Campanha</Th>
                <Th>Template</Th>
                <Th>Status</Th>
                <Th isNumeric>Enviados</Th>
                <Th isNumeric>Entregues</Th>
                <Th isNumeric>Taxa Abertura</Th>
                <Th isNumeric>Taxa Cliques</Th>
                <Th>Data Envio</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {campaigns.length === 0 ? (
                <Tr>
                  <Td colSpan={9} textAlign="center" py={8}>
                    <Text color="gray.500">Nenhuma campanha encontrada</Text>
                  </Td>
                </Tr>
              ) : (
                campaigns.map((campaign) => (
                  <Tr key={campaign.id} _hover={{ bg: 'gray.50' }}>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">{campaign.name}</Text>
                        <Text fontSize="sm" color="gray.600" noOfLines={1}>
                          {campaign.subject}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Badge variant="outline">{campaign.templateId}</Badge>
                    </Td>
                    <Td>{getStatusBadge(campaign.status)}</Td>
                    <Td isNumeric>{campaign.metrics.totalSent}</Td>
                    <Td isNumeric>
                      <VStack align="end" spacing={0}>
                        <Text>{campaign.metrics.totalDelivered}</Text>
                        {campaign.metrics.totalSent > 0 && (
                          <Progress 
                            value={(campaign.metrics.totalDelivered / campaign.metrics.totalSent) * 100} 
                            size="xs" 
                            colorScheme="green"
                            width="60px"
                          />
                        )}
                      </VStack>
                    </Td>
                    <Td isNumeric>
                      <HStack justify="flex-end" spacing={1}>
                        <MdOpenInNew size={14} color="#3182ce" />
                        <Text color={campaign.metrics.openRate > 20 ? 'green.500' : 'gray.700'}>
                          {formatPercentage(campaign.metrics.openRate)}
                        </Text>
                      </HStack>
                    </Td>
                    <Td isNumeric>
                      <HStack justify="flex-end" spacing={1}>
                        <MdTouchApp size={14} color="#3182ce" />
                        <Text color={campaign.metrics.clickRate > 5 ? 'green.500' : 'gray.700'}>
                          {formatPercentage(campaign.metrics.clickRate)}
                        </Text>
                      </HStack>
                    </Td>
                    <Td fontSize="sm">{formatDate(campaign.startedAt)}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<FaEye />}
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={() => viewCampaignDetails(campaign.id)}
                          aria-label="Ver detalhes"
                          title="Ver detalhes"
                        />
                        <IconButton
                          icon={<FaDownload />}
                          size="sm"
                          colorScheme="green"
                          variant="ghost"
                          onClick={() => exportCampaignReport(campaign.id)}
                          aria-label="Exportar CSV"
                          title="Exportar CSV"
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

export default CampaignAnalytics;